import auStates from 'client/util/Constants/auStates';
import caProvinces from 'client/util/Constants/caProvinces';
import usStates from 'client/util/Constants/usStates';

const formInputFields = {
  firstName: {
    label: 'First Name',
    name: 'first name',
  },
  lastName: {
    label: 'Last Name',
    name: 'last name',
  },
  partner: {
    label: 'I am a',
    name: 'partner',
  },
  partnerType: {
    label: 'My primary business function is',
    name: 'partner type',
  },
  companyName: {
    label: 'Company Name',
    name: 'company name',
  },
  companySize: {
    label: 'Company Size',
    name: 'company size',
  },
  endpointPotential: {
    label: 'Number of Endpoints Under Management',
    name: 'endpoint potential',
  },
  country: {
    label: 'Where is your company located?',
    name: 'country',
  },
  state: {
    label: 'State / Province Label', // This is a generic label that is specifically used for the segment event. The actual label that is displayed on the front end is determined by the selectedCountry computed property.
    name: 'state',
  },
  doubleOptIn: {
    label: 'Check this box if you would like to opt out of receiving marketing communications',
    name: 'doubleOptIn',
  },
  dataCenter: {
    helperText: 'Choose carefully, the selected region cannot be changed.',
    label: 'Primary Data Storage', // This is a generic label that is specifically used for the segment event.
    name: 'dataCenter',
  },
  email: {
    label: 'Company Email',
    name: 'email',
  },
  phone: {
    label: 'Phone',
    name: 'phone',
  },
  hasPhonePreference: {
    label: 'I prefer to be contacted by phone',
    name: 'hasPhonePreference',
  },
  termsOfService: {
    name: 'termsOfService',
    label: 'Terms of Service', // This is a generic label that is specifically used for the segment event. The actual label that is displayed on the front end is determined by the handleLabel computed property.
  },
};

const areaOfInterestValues = {
  cloudBasedLDAPorRADIUSServices: 'Cloud Based LDAP or RADIUS Services',
  office365: 'Office 365 or Google Apps Integration',
  other: 'other',
  partnerReseller: 'Partner/Reseller',
  sso: 'Single Sign-On (SSO)',
  deviceManagement: 'Device Mgmt',
  systemManagement: 'System & Server User Mgmt (Mac, Linux, Windows)',
};

/**
 * Names of login button types
 */
const buttonTypes = {
  error: 'error',
};

/**
 * Names of the different client types
 */
const clientType = {
  admin: 'admin',
  user: 'user',
};

/**
 * Name of cookie that determines whether the user or admin page is displayed
 */
const clientTypeCookie = 'jc_prevLoginType';

/**
 * Name of cookie that determines whether the user or admin page is displayed
 */
const confirmPartnerRouteName = 'SignupConfirmPartner';

// This is a reference value for data center routing comparison, not a URL construction
// eslint-disable-next-line @jumpcloud/ui/no-hardcoded-domain
const US_DATA_CENTER_ENDPOINT = 'console.jumpcloud.com';

const dataCenterRegions = {
  us: {
    endpoint: US_DATA_CENTER_ENDPOINT,
    environment: 'prd',
    label: 'United States',
    countries: [], // US is the default, not tied to specific countries
  },
  eu: {
    endpoint: 'console.eu.jumpcloud.com',
    environment: 'eu',
    label: 'European Union',
    countries: [
      'Austria',
      'Belgium',
      'Bulgaria',
      'Croatia',
      'Cyprus',
      'Czech Republic',
      'Denmark',
      'Estonia',
      'Finland',
      'France',
      'Germany',
      'Greece',
      'Hungary',
      'Iceland',
      'Ireland',
      'Italy',
      'Latvia',
      'Lithuania',
      'Luxembourg',
      'Malta',
      'Netherlands',
      'Norway',
      'Poland',
      'Portugal',
      'Romania',
      'Slovakia',
      'Slovenia',
      'Spain',
      'Sweden',
      'Switzerland',
      'United Kingdom',
    ],
  },
  in: {
    endpoint: 'console.in.jumpcloud.com',
    environment: 'in',
    label: 'India',
    countries: [
      'Australia',
      'Bangladesh',
      'Bhutan',
      'Brunei Darussalam',
      'Cambodia',
      'China',
      'Cook Islands',
      'Fiji',
      'French Polynesia',
      'India',
      'Indonesia',
      'Japan',
      'Kiribati',
      'Korea, Republic of',
      'Lao People\'s Democratic Republic',
      'Macao',
      'Malaysia',
      'Maldives',
      'Mongolia',
      'Myanmar',
      'Nauru',
      'Nepal',
      'New Caledonia',
      'New Zealand',
      'Niue',
      'Pakistan',
      'Papua New Guinea',
      'Philippines',
      'Samoa',
      'Singapore',
      'Solomon Islands',
      'Sri Lanka',
      'Taiwan',
      'Thailand',
      'Timor-Leste',
      'Tonga',
      'Tuvalu',
      'Vanuatu',
      'Viet Nam',
    ],
  },
};

/*
 * Partnership Names for partner verifications
 */
const partnershipNames = {
  crowdStrike: 'crowdstrike',
};

/**
 * Names of Google Tag Manager events
 */
const gtmEvents = {
  accountVerified: { event: 'Account Verified' },
  accountVerifiedBusinessDomain: { event: 'Account Verified Business Domain' },
  accountVerifiedFreeEmail: { event: 'Account Verified Free Email' },
  freeAccountTrackingEvent: { event: 'Free Account Created' },
  freeAccountMSPTrackingEvent: { event: 'Free Account MSP Created' },
  googleEnhancedConversionDataEvent: { event: 'Google Enhanced Conversion Data' },
  googleTrackingEvent: { event: 'new-google-signup' },
};

const freeEmails = ['@gmail', '@hotmail', '@yahoo'];

const sizeValues = {
  size0to10: '0-10',
  size11to50: '11-50',
  size51to100: '51-100',
  size101to200: '101-200',
  size201to300: '201-300',
  size301to400: '301-400',
  size401to500: '401-500',
  size501to1000: '501-1000',
  size1001to2000: '1001-2000',
  size2001to5000: '2001-5000',
  size5001plus: '5001+',
};

const endpointValues = {
  endpoints1to50: '1-50',
  endpoints51to100: '51-100',
  endpoints101to250: '101-250',
  endpoints251to500: '251-500',
  endpoints501to1000: '501-1,000',
  endpoints1001to2500: '1,001-2,500',
  endpoints2501to5000: '2,501-5,000',
  endpoints5001to10000: '5,001-10,000',
  endpoints10001to25000: '10,001-25,000',
  endpoints25001to50000: '25,001-50,000',
  endpoints50001to100000: '50,001-100,000',
  endpoints100000plus: '>100,000',
};

const optOutCountries = [
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Ireland',
  'Italy',
  'Latvia',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  'Iceland',
  'Norway',
  'United Kingdom',
  'Liechtenstein',
  'Switzerland',
  'Canada',
  'Australia',
];

const signupDisabledCountries = [
  'Belarus',
  'Russian Federation',
];

const countryStates = {
  us: {
    label: 'State',
    placeholder: 'Select a State',
    stateList: usStates,
  },
  ca: {
    label: 'Province',
    placeholder: 'Select a Province',
    stateList: caProvinces,
  },
  au: {
    label: 'State',
    placeholder: 'Select a State',
    stateList: auStates,
  },
};

/**
 * Messages that the UI displays after responses are received
 */
const responseMessages = {
  adminGoogleRegistrationError: 'An error occurred while creating your account.',
};

const thirdPartySources = {
  google: { thirdPartySource: 'Google' },
};

const valuePropIconNames = {
  activeDirectory: 'IconActiveDirectory',
  balloon: 'IconBalloon',
  cloud: 'IconCloud',
  computer: 'IconComputer',
  lock: 'IconLock',
  windows: 'IconWindows',
};

const partnerValues = {
  distributor: 'Distributor',
  msp: 'MSP',
  var: 'VAR',
};

const useCaseValues = {
  buildCloudDirectory: 'Build Cloud Directory',
  implementZeroTrust: 'Zero Trust Security',
  lookingAround: 'I’m just looking around',
  notProvided: 'Not Provided',
  remoteWork: 'Remote Work',
  replaceDirectory: 'Replace AD',
};

const defaultValueProps = [
  {
    name: 'centralize',
    iconName: valuePropIconNames.cloud,
    text: 'Centralize identities',
  },
  {
    name: 'authenticate',
    iconName: valuePropIconNames.lock,
    text: 'Authenticate users to all resources',
  },
  {
    name: 'devices',
    iconName: valuePropIconNames.computer,
    text: 'Strengthen your org’s security practices ',
  },
];

const useCaseInfoBlockValues = {
  [useCaseValues.buildCloudDirectory]: {
    title: 'No Directory? Build One Now!',
    step2: {
      data: {
        valueProps: [
          {
            name: 'centralize',
            iconName: valuePropIconNames.cloud,
            text: 'Centralize identities',
          },
          {
            name: 'authenticate',
            iconName: valuePropIconNames.lock,
            text: 'Authenticate users to all resources',
          },
          {
            name: 'devices',
            iconName: valuePropIconNames.computer,
            text: 'Manage Mac, Windows, and Linux devices',
          },
        ],
      },
    },
    step3: {
      data: {
        quote: 'JumpCloud is really empowering us to let our people work from anywhere.',
        attribution: '- Mitch Anderson, Director of Systems Engineering, Boulder Heavy Industries',
      },
    },
    step4: {
      data: {
        quote: 'Although the last few months have been extremely challenging for our organization and employees, I cannot imagine what it would have been like without JumpCloud to bring everything together in such a complete manner quickly and efficiently, while also allowing us to do so from a safe distance.',
        attribution: '- Justin Price, IT Manager, Chase International',
      },
    },
  },
  [useCaseValues.replaceDirectory]: {
    title: 'Looking for an Active Directory Alternative? Start Here.',
    step2: {
      data: {
        valueProps: [
          {
            name: 'activedirectory',
            iconName: valuePropIconNames.activeDirectory,
            text: 'Import Active Directory users',
          },
          {
            name: 'windows',
            iconName: valuePropIconNames.windows,
            text: 'Convert Windows domain accounts to local accounts',
          },
          {
            name: 'extendidentities',
            iconName: valuePropIconNames.computer,
            text: 'Extend AD identities to all resources',
          },
        ],
      },
    },
    step3: {
      data: {
        quote: 'I’m a big supporter of moving into the cloud, and I think the sooner, the better. Everybody knows that the days of the domain controller and Active Directory are gone. It’s just a matter of phasing them out. My next piece of advice would be to try JumpCloud.',
        attribution: '- Enrique Salgado, Global IT Director, Cabify',
      },
    },
    step4: {
      data: {
        quote: 'IT is all going to the cloud, at least for a lot of businesses. It doesn’t make sense to spend 90% of your time in those areas but then have to come back and deal with on-prem systems.',
        attribution: '- Todd Wade, Head of Information Security & Compliance, PayWith',
      },
    },
  },
  [useCaseValues.implementZeroTrust]: {
    title: 'Get a Modern Model for Security and Compliance!',
    step2: {
      data: {
        valueProps: [
          {
            name: 'access',
            iconName: valuePropIconNames.lock,
            text: 'Scope user and device access',
          },
          {
            name: 'mfa',
            iconName: valuePropIconNames.lock,
            text: 'Require MFA everywhere',
          },
          {
            name: 'policies',
            iconName: valuePropIconNames.lock,
            text: 'Layer Conditional Access policies',
          },
        ],
      },
    },
    step3: {
      data: {
        quote: 'JumpCloud means that we save time and effort ensuring our access is controlled across all our environments.',
        attribution: '- Ian Wharton, WAN Specialist, Principle Networks',
      },
    },
    step4: {
      data: {
        quote: 'With JumpCloud’s Conditional Access policies, we can strengthen our Zero Trust posture with trusted identity, network, and device, and manage it through a single Admin portal.',
        attribution: '- Brian Coleman, Founder, MatchstickBHM',
      },
    },
  },
  [useCaseValues.remoteWork]: {
    title: 'Users and Devices Outside the Office? No Problem.',
    step2: {
      data: {
        valueProps: [
          {
            name: 'onboarding',
            iconName: valuePropIconNames.cloud,
            text: 'Onboard users and devices anywhere',
          },
          {
            name: 'remoteaccess',
            iconName: valuePropIconNames.lock,
            text: 'Provide secure remote user access',
          },
          {
            name: 'management',
            iconName: valuePropIconNames.balloon,
            text: 'Manage everything from a web-based console',
          },
        ],
      },
    },
    step3: {
      data: {
        quote: 'JumpCloud is a business necessity for us now. When we had to switch to 100% remote work within hours, JumpCloud made the shift easy, letting us manage users, their devices and systems, and everything on them. It gives us control over the entire ecosystem of our IT resources and the connections between them.',
        attribution: '- Van Romine, IT Manager, Inland Valleys Association of Realtors',
      },
    },
    step4: {
      data: {
        quote: 'Our goal is to create a corporate IT environment where location doesn’t matter. Remote work is easy. Spinning up a new office is easy. Moving offices is easy. Ideally for us, an Uplight office is just a building with an internet connection, and that’s it.',
        attribution: '- Andy Wing, IT Manager, Uplight',
      },
    },
  },
  [useCaseValues.lookingAround]: {
    title: 'Welcome to JumpCloud!',
    step2: {
      data: {
        valueProps: defaultValueProps,
      },
    },
    step3: {
      data: {
        quote: 'JumpCloud continues to deliver a high return on investment for my department, my users, and the company as a whole.',
        attribution: '- Justin Price, IT Manager, Chase International',
      },
    },
    step4: {
      data: {
        quote: 'The best technology is the one that just works. That’s how I think about JumpCloud: It’s an invisible, essential resource. I don’t worry about it. I don’t think twice about it. I just depend on it.',
        attribution: '- Van Romine, IT Manager, Inland Valleys Association of Realtors',
      },
    },
  },
};

const SignupConfirmationMessages = {
  alreadyConfirmed: 'This link is already confirmed. Please wait for JumpCloud to setup your account.',
  alreadyConfirmedResponseErrorMessage: 'Bad Request: Previously confirmed registration code',
  alreadyConfirmedResponse: 'reconfirm',
  expired: 'This link has expired.',
  expiredLinkText: 'Resend email',
  invalid: 'That link was invalid. Give it another try or',
  invalidLinkText: 'resend email',
  resending: 'Resending verification email.',
  resendError: 'Unable to resend the verification email.',
  resent: 'Check your email for your new activation link',
  tryAgain: 'Try again',
  verifying: 'Verifying your account',
};

const SignupText = {
  headerText: 'We noticed you don\'t have a Google account connected.',
  subHeaderText: 'The email provided does not appear to be associated with a current organization.',
  createNewOrg: 'Creating your Account...',
  subHeaderCreateText: 'Just a moment while we create your JumpCloud account',
  errorHeaderText: 'Oops, something went wrong.',
};

const SignupOptions = {
  areaOfInterestOptions: [
    { name: 'Cloud-based LDAP or RADIUS Service', value: areaOfInterestValues.cloudBasedLDAPorRADIUSServices },
    { name: 'Office 365 Integration or G Suite Integration', value: areaOfInterestValues.office365 },
    { name: 'Other', value: areaOfInterestValues.other },
    { name: 'Partner/Reseller', value: areaOfInterestValues.partnerReseller },
    { name: areaOfInterestValues.sso, value: areaOfInterestValues.sso },
    { name: 'System Management', value: areaOfInterestValues.deviceManagement },
    { name: 'System and Server User Management', value: areaOfInterestValues.systemManagement },
  ],
  companySizeOptions: [
    { name: sizeValues.size0to10, value: sizeValues.size0to10 },
    { name: sizeValues.size11to50, value: sizeValues.size11to50 },
    { name: sizeValues.size51to100, value: sizeValues.size51to100 },
    { name: sizeValues.size101to200, value: sizeValues.size101to200 },
    { name: sizeValues.size201to300, value: sizeValues.size201to300 },
    { name: sizeValues.size301to400, value: sizeValues.size301to400 },
    { name: sizeValues.size401to500, value: sizeValues.size401to500 },
    { name: sizeValues.size501to1000, value: sizeValues.size501to1000 },
    { name: sizeValues.size1001to2000, value: sizeValues.size1001to2000 },
    { name: sizeValues.size2001to5000, value: sizeValues.size2001to5000 },
    { name: sizeValues.size5001plus, value: sizeValues.size5001plus },
  ],
  endpointPotentialOptions: [
    { name: endpointValues.endpoints1to50, value: endpointValues.endpoints1to50 },
    { name: endpointValues.endpoints51to100, value: endpointValues.endpoints51to100 },
    { name: endpointValues.endpoints101to250, value: endpointValues.endpoints101to250 },
    { name: endpointValues.endpoints251to500, value: endpointValues.endpoints251to500 },
    { name: endpointValues.endpoints501to1000, value: endpointValues.endpoints501to1000 },
    { name: endpointValues.endpoints1001to2500, value: endpointValues.endpoints1001to2500 },
    { name: endpointValues.endpoints2501to5000, value: endpointValues.endpoints2501to5000 },
    { name: endpointValues.endpoints5001to10000, value: endpointValues.endpoints5001to10000 },
    { name: endpointValues.endpoints10001to25000, value: endpointValues.endpoints10001to25000 },
    { name: endpointValues.endpoints25001to50000, value: endpointValues.endpoints25001to50000 },
    { name: endpointValues.endpoints50001to100000, value: endpointValues.endpoints50001to100000 },
    { name: endpointValues.endpoints100000plus, value: endpointValues.endpoints100000plus },
  ],
  partnerOptions: [
    { name: partnerValues.distributor, value: partnerValues.distributor },
    { name: 'Value Added Reseller (VAR)', value: partnerValues.var },
    { name: 'Managed IT Service Provider (MSP)', value: partnerValues.msp },
  ],
  partnerTypeOptions: [
    { name: 'In-house IT Person', value: 'No' },
    { name: 'IT Service Provider (MSP) / VAR / Distributor', value: 'Yes' },
  ],
  useCaseOptionsSignupExperiment: [
    {
      name: useCaseValues.buildCloudDirectory,
      iconName: valuePropIconNames.cloud,
      value: useCaseValues.buildCloudDirectory,
    },
    {
      name: useCaseValues.replaceDirectory,
      iconName: valuePropIconNames.windows,
      value: useCaseValues.replaceDirectory,
    },
    {
      name: useCaseValues.implementZeroTrust,
      iconName: valuePropIconNames.lock,
      value: useCaseValues.implementZeroTrust,
    },
    {
      name: useCaseValues.remoteWork,
      iconName: valuePropIconNames.computer,
      value: useCaseValues.remoteWork,
    },
    {
      name: 'I\'m Just Looking Around',
      iconName: valuePropIconNames.balloon,
      value: useCaseValues.lookingAround,
    },
  ],

  // If name and value don't match then triggerUseCaseSelected needs to be updated to properly
  // submit the value for dropdownSelection
  useCaseOptions: [
    { name: useCaseValues.buildCloudDirectory, value: useCaseValues.buildCloudDirectory },
    { name: useCaseValues.remoteWork, value: useCaseValues.remoteWork },
    { name: useCaseValues.replaceDirectory, value: useCaseValues.replaceDirectory },
    { name: useCaseValues.implementZeroTrust, value: useCaseValues.implementZeroTrust },
    { name: useCaseValues.lookingAround, value: useCaseValues.lookingAround },
  ],
};

export { dataCenterRegions, US_DATA_CENTER_ENDPOINT };

export const termsOfServiceMessage = {
  general: 'By creating your account, you agree to the JumpCloud [Privacy Policy](https://jumpcloud.com/privacy) and [Terms of Service](https://jumpcloud.com/legal). This site is protected by reCAPTCHA and the Google [Privacy Policy](https://policies.google.com/privacy) and [Terms of Service](https://policies.google.com/terms) apply.',
  protected: 'By creating this account, I agree to the JumpCloud [Terms of Service](https://jumpcloud.com/legal) and [Privacy Policy](https://jumpcloud.com/privacy) and to the processing and international transfer of my personal data by JumpCloud as described in the [Privacy Policy](https://jumpcloud.com/privacy). This site is protected by reCAPTCHA and the Google [Privacy Policy](https://policies.google.com/privacy) and [Terms of Service](https://policies.google.com/terms) apply.',
};

const SignupHeading = {
  general: 'Start your 30-day trial of JumpCloud now',
  msp: 'Create your JumpCloud for MSPs™ account with 10 free NFR licenses',
};

export default {
  areaOfInterestValues,
  buttonTypes,
  clientType,
  clientTypeCookie,
  confirmPartnerRouteName,
  countryStates,
  dataCenterRegions,
  formInputFields,
  freeEmails,
  gtmEvents,
  optOutCountries,
  partnershipNames,
  responseMessages,
  SignupConfirmationMessages,
  signupDisabledCountries,
  SignupHeading,
  SignupOptions,
  SignupText,
  termsOfServiceMessage,
  thirdPartySources,
  US_DATA_CENTER_ENDPOINT,
  useCaseInfoBlockValues,
  useCaseValues,
};
